import { type TAudioPaginated, type TCollection, type TCollectionSimple } from '@core/models'

import { queryClient } from '@core/query'
import { CollectionQueryKey } from './queryKeys'

export const addCollection = ({
    username,
    collection,
}: {
    username: string
    collection: TCollection
}) => {
    queryClient.setQueryData<TCollection[]>(
        CollectionQueryKey.authorCollections(username),
        collections => (collections ? [collection, ...collections] : collections),
    )
}

export const getStoredCollection = (username: string, slug: string) => {
    return queryClient.getQueryData<TCollection>(
        CollectionQueryKey.authorCollection(username, slug),
    )
}

export const getStoredCollections = (username: string) => {
    return queryClient.getQueryData<TCollection[]>(CollectionQueryKey.authorCollections(username))
}

export const setCollection = ({
    username,
    slug,
    collection,
}: {
    username: string
    slug: string
    collection: TCollection
}) => {
    queryClient.setQueryData<TCollection>(
        CollectionQueryKey.authorCollection(username, slug),
        collection,
    )
}

export const setCollectionSimple = ({
    username,
    slug,
    collection,
}: {
    username: string
    slug: string
    collection: TCollectionSimple
}) => {
    queryClient.setQueryData<TCollectionSimple>(
        CollectionQueryKey.authorCollectionSimple(username, slug),
        collection,
    )
}

export const setCollections = ({
    username,
    collections,
}: {
    username: string
    collections: TCollection[]
}) => {
    queryClient.setQueryData<TCollection[]>(
        CollectionQueryKey.authorCollections(username),
        collections,
    )
}

export const removeCollectionFromCollections = ({
    username,
    id,
}: {
    username: string
    id: string
}) => {
    const collections = getStoredCollections(username)

    if (!collections) return undefined

    const newCollections = collections.filter(collection => collection.id !== id)

    setCollections({ username, collections: newCollections })

    return newCollections
}

export const revalidateCollection = (username: string, slug: string) => {
    queryClient.invalidateQueries({
        queryKey: CollectionQueryKey.authorCollection(username, slug),
    })

    queryClient.invalidateQueries({
        queryKey: CollectionQueryKey.authorCollections(username),
        exact: true,
    })

    queryClient.invalidateQueries({
        queryKey: CollectionQueryKey.authorPaginatedCollections(username),
    })
}

export const revalidatePaginatedCollections = (username: string) => {
    queryClient.invalidateQueries({
        queryKey: CollectionQueryKey.authorPaginatedCollections(username),
    })
}

// Prefix-invalidate every page of a collection's tracklist (`collectionAudios` is keyed by page), so
// an owner edit refetches the on-screen list instead of leaving the loader-rendered page stale.
export const revalidateCollectionAudios = (username: string, slug: string) => {
    queryClient.invalidateQueries({
        queryKey: CollectionQueryKey.collectionAudiosPrefix(username, slug),
    })
}

export const setCollectionAudios = ({
    username,
    slug,
    audios,
}: {
    username: string
    slug: string
    audios: TAudioPaginated
}) => {
    queryClient.setQueryData<TAudioPaginated>(
        CollectionQueryKey.collectionAudios(username, slug, audios.pagination.page),
        audios,
    )
}

export const revalidateAllCollectionAudios = () => {
    queryClient.invalidateQueries({ queryKey: CollectionQueryKey.anyCollectionAudios() })
}

export const revalidateCollections = (username: string) => {
    queryClient.invalidateQueries({
        queryKey: CollectionQueryKey.authorCollections(username),
        exact: true,
    })

    // invalidate all author collections  queries
    queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
            return (
                queryKey[0] === 'author' &&
                queryKey[1] === username &&
                queryKey[2] === 'collections' &&
                !!queryKey[3]
            )
        },
    })

    revalidatePaginatedCollections(username)
}

export const cancelCollectionQuery = (username: string, slug: string) => {
    return queryClient.cancelQueries({
        queryKey: CollectionQueryKey.authorCollection(username, slug),
    })
}
